import { Component, Input, Output, EventEmitter, inject, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Entity } from '../../../../core/data-models/Entity';
import { EntityGroup } from '../../../../core/data-models/entity-group';
import { LanguageService } from '../../../../core/services/language.service';
import { ModuleName } from '../../../../shared/enums/ModuleName';
import { ThemeModule } from '../../../../shared/Theme/theme.module';
import { ChildrenAnimation, inOutAnimation, slidesAniamtion } from '../../../../shared/animations/allAnimations';

export interface EntitiesTableOptions {
    data: EntityGroup[];
    columns: { field: string; title: string }[];
}

/**
 * Company Structure Settings Component
 * 
 * Handles entity group management including:
 * - Display entity groups in a table
 * - Add new entity group
 * - Edit existing entity group
 * - Delete entity group
 */
@Component({
    selector: 'company-structure-settings',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ThemeModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    templateUrl: './company-structure-settings.component.html',
    styleUrls: ['./company-structure-settings.component.scss'],
    animations: [inOutAnimation, slidesAniamtion, ChildrenAnimation]
})
export class CompanyStructureSettingsComponent {
    private languageService = inject(LanguageService);

    readonly ModuleNames = ModuleName;

    @Input() selectedEntity!: Entity;
    @Input() language: string = 'EN';
    @Input() entitiesOptions!: EntitiesTableOptions;

    @Output() addEntityGroup = new EventEmitter<void>();
    @Output() editEntityGroup = new EventEmitter<EntityGroup>();
    @Output() deleteEntityGroup = new EventEmitter<{ entityGroup: EntityGroup; index: number }>();

    /**
     * Get entity group name in current language
     */
    getEntityGroupName(entityGroup: EntityGroup): string {
        const groupName = entityGroup.name.find(name => name.key === this.language)?.name;
        if (!groupName) {
            return entityGroup.name.find(name => name.name !== '')?.name || '';
        }
        return groupName;
    }

    onAddEntityGroup(): void {
        this.addEntityGroup.emit();
    }

    onEditEntityGroup(entityGroup: EntityGroup): void {
        this.editEntityGroup.emit(entityGroup);
    }

    onDeleteEntityGroup(entityGroup: EntityGroup, index: number): void {
        this.deleteEntityGroup.emit({ entityGroup, index });
    }
}
